import { type ReactNode, forwardRef } from 'react';

import {
  type DOMProps,
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

//#region Typings
/**
 * Accepted properties for the PasswordInput tooltip
 * @public
 */
export interface PasswordInputTooltipProps
  extends DOMProps,
    StylingProps,
    DataTestId {
  /** Elements to be displayed in the PasswordInput tooltip slot, preferably an icon or a similar visual element */
  children?: ReactNode;
  /**
   * If specified, the tooltip is rendered with this text when the password is shown, which describes what the trigger does.
   * Please also consider translating the text in an internationalized context and use a short text description for the tooltip.
   */
  showText?: string;
  /**
   * If specified, the tooltip is rendered with this text when the password is hidden, which describes what the trigger does.
   * Please also consider translating the text in an internationalized context and use a short text description for the tooltip.
   */
  hideText?: string;
}

//#endregion

/**
 * Component which allows users to configure the tooltip text and icons of the PasswordInput.
 * @public
 */
export const PasswordInputTooltip = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  PasswordInputTooltipProps
>((props, forwardedRef) => {
  return <div ref={forwardedRef} {...props} />;
});

(
  PasswordInputTooltip as typeof PasswordInputTooltip & { displayName: string }
).displayName = 'PasswordInput.Tooltip';
